import { useAtomValue } from "@effect/atom-react";
import { DocumentChangesSnapshot, type DocumentChangesView } from "@repo/shared/findingChanges";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { fetchComplianceJson } from "./complianceFetch";
import { complianceQueryAtom } from "./complianceLive";

const decode = Schema.decodeUnknownEffect(DocumentChangesSnapshot);
export const findingChangesAtom = Atom.family((key: string) => {
  const [organizationId, policyId, reviewId, kind = "finding"] = JSON.parse(key) as [
    string,
    string,
    string,
    ("finding" | "recommendation")?,
  ];
  return complianceQueryAtom(
    organizationId,
    Effect.gen(function* () {
      if (!organizationId || !policyId || !reviewId) return null;
      return yield* fetchComplianceJson(
        resolvePrimaryEnvironmentHttpUrl(`/api/amoreg/regulation/${kind}-changes`, {
          organizationId,
          policyId,
          [kind === "finding" ? "findingId" : "recommendationId"]: reviewId,
        }),
        decode,
      );
    }),
  );
});
export function useFindingChanges(
  organizationId: string,
  policyId: string,
  findingId: string,
  kind: "finding" | "recommendation" = "finding",
) {
  const atom = useMemo(
    () => findingChangesAtom(JSON.stringify([organizationId, policyId, findingId, kind])),
    [organizationId, policyId, findingId, kind],
  );
  const result = useAtomValue(atom);
  const value = AsyncResult.value(result);
  const data = value._tag === "Some" ? value.value : null;
  const unavailable = result._tag === "Failure" || Boolean(data && "unavailable" in data);
  const refresh = useCallback(() => appAtomRegistry.refresh(atom), [atom]);
  return {
    proposal: data && !("unavailable" in data) ? data.proposal : null,
    loading: result._tag === "Initial" || (result.waiting && !data),
    unavailable,
    refresh,
  };
}
export function findingChangesFileUrl(
  organizationId: string,
  policyId: string,
  findingId: string,
  proposalId: string,
  side: "original" | "proposed",
) {
  return resolvePrimaryEnvironmentHttpUrl("/api/amoreg/regulation/finding-changes-file", {
    organizationId,
    policyId,
    findingId,
    proposalId,
    side,
  });
}

export function documentChangesFileUrl(
  organizationId: string,
  proposal: DocumentChangesView,
  side: "original" | "proposed",
) {
  if ("findingId" in proposal)
    return findingChangesFileUrl(
      organizationId,
      proposal.policyId,
      proposal.findingId,
      proposal.id,
      side,
    );
  return resolvePrimaryEnvironmentHttpUrl("/api/amoreg/regulation/recommendation-changes-file", {
    organizationId,
    policyId: proposal.policyId,
    recommendationId: proposal.recommendationId,
    proposalId: proposal.id,
    side,
  });
}
